import React, { useCallback } from 'react';

import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import { L1_MENU_GROUPS } from '@atlassian/navigation-common';
import { DragIndicatorWrapper, useL1ItemDragAndDrop } from '@atlassian/navigation-drag-and-drop';

import type {
	GlobalShortcutComponentItem,
	GlobalShortcutComponentItemBase,
	GlobalShortcutComponentItemDraggable,
} from '../../types';

export type MenuComponentItemProps = {
	dragAndDropEnabled?: boolean;
	/**
	 * Whether the shortcut is being rendered in the navigation "More" menu.
	 * Passed down to the inner custom component for any rendering adjustments it might need to do, such as whether to render spotlights.
	 */
	isInMoreMenu?: boolean;
	/**
	 * Optional additional click handler (in addition to the one provided in the shortcut data)
	 */
	onClick?: () => void;
	/**
	 * The shortcut config object; basically a MappedGlobalShortcutsItem that *must* be of the "component" type
	 */
	shortcut: GlobalShortcutComponentItem & { key: string };
};

type MenuComponentDraggableItemProps = Omit<MenuComponentItemProps, 'shortcut'> & {
	shortcut: GlobalShortcutComponentItemDraggable;
};

type MenuComponentNonDraggableItemProps = Omit<MenuComponentItemProps, 'shortcut'> & {
	shortcut: GlobalShortcutComponentItemBase;
};

const isMenuComponentDraggableItemConfig = (
	shortcut: MenuComponentItemProps['shortcut'],
): shortcut is MenuComponentDraggableItemProps['shortcut'] => {
	return !!(shortcut?.isReorderable && shortcut?.menuItemId);
};

export function MenuComponentItem({
	shortcut,
	onClick,
	dragAndDropEnabled,
	isInMoreMenu,
}: MenuComponentItemProps) {
	const handleClick = useCallback(() => {
		onClick?.();
		shortcut.onClick?.();
	}, [onClick, shortcut]);

	if (dragAndDropEnabled && isMenuComponentDraggableItemConfig(shortcut)) {
		return (
			<MenuComponentDraggableItem
				shortcut={shortcut}
				onClick={handleClick}
				isInMoreMenu={isInMoreMenu}
			/>
		);
	}

	return (
		<MenuComponentNonDraggableItem
			onClick={handleClick}
			shortcut={shortcut}
			isInMoreMenu={isInMoreMenu}
		/>
	);
}

function MenuComponentNonDraggableItem({
	shortcut,
	onClick,
	isInMoreMenu,
}: MenuComponentNonDraggableItemProps) {
	const ItemWrapper = shortcut?.ItemWrapper ?? React.Fragment;
	const Component = shortcut.component;

	return (
		<ItemWrapper>
			<Component onClick={onClick} isInMoreMenu={isInMoreMenu} />
		</ItemWrapper>
	);
}

function MenuComponentDraggableItem({
	shortcut,
	onClick,
	isInMoreMenu,
}: MenuComponentDraggableItemProps) {
	const Component = shortcut.component;
	const ItemWrapper = shortcut?.ItemWrapper ?? React.Fragment;

	const { draggableRef, isDragging, maybeDragPreview, dropTargetRef, dropIndicator } =
		useL1ItemDragAndDrop({
			id: shortcut.menuItemId,
			isExpandable: false,
			isExpandOrFlyoutOpen: false,
			l1DragAndDropEnabled: true,
			onExpandFlyoutToggle: () => {},
			peek: false,
			item: {
				type: 'custom',
				elemBefore: null,
				label: shortcut.label,
				labelElem: shortcut.labelElem,
				isReorderable: shortcut.isReorderable,
				Component: Component,
				isDraggable: true,
			},
			group: L1_MENU_GROUPS.SHORTCUTS,
		});

	if (draggableRef === undefined || dropIndicator === undefined || dropTargetRef === undefined) {
		return (
			<MenuComponentNonDraggableItem
				shortcut={shortcut}
				onClick={onClick}
				isInMoreMenu={isInMoreMenu}
			/>
		);
	}

	return (
		<DragIndicatorWrapper hasDragIndicator>
			<ItemWrapper>
				<Component
					draggableRef={mergeRefs([draggableRef, shortcut?.ref])}
					dropIndicator={dropIndicator}
					hasDragIndicator={true}
					isDragging={isDragging}
					visualContentRef={dropTargetRef}
					onClick={onClick}
					isInMoreMenu={isInMoreMenu}
				/>
			</ItemWrapper>
			{maybeDragPreview}
		</DragIndicatorWrapper>
	);
}
